import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import type { Bucket } from "../types";
import type { ChartDataPoint, ScoreMetric } from "../utils/chartData";
import type { DrillClick } from "../utils/drilldown";
import { CRITERIA_COLORS, SCORE_METRIC_OPTIONS, computeScoreYDomain } from "../utils/chartData";
import { formatDisplayDate } from "../utils/chartFormatters";
import { EmptyState } from "../../../ui/EmptyState";
import { ChartSkeleton } from "../../../ui/ChartSkeleton";
import { useChartTheme } from "../../theme";
import { LineChartTooltip } from "./charts/LineChartTooltip";
import { makeClickableDot } from "./charts/clickableDot";

interface EvaluationsChartProps {
  dataPoints: ChartDataPoint[];
  criteriaNames: string[];
  /** criteria → display label (evaluator name); falls back to criteria. */
  labels?: Record<string, string>;
  bucket: Bucket;
  loading?: boolean;
  /** Which score metric the lines plot (avg | p25 | p10). Default "avg". */
  metric?: ScoreMetric;
  /** When provided, renders the metric toggle in the header. */
  onMetricChange?: (metric: ScoreMetric) => void;
  /** When provided, clicking a point drills into the filtered Traces view. */
  onDrill?: (click: DrillClick) => void;
}

/**
 * Chart component for visualizing evaluation scores over time.
 * Consumes pre-aggregated data from /insights/aggregate.
 */
export function EvaluationsChart({
  dataPoints,
  criteriaNames,
  labels = {},
  bucket,
  loading = false,
  metric = "avg",
  onMetricChange,
  onDrill,
}: EvaluationsChartProps) {
  const chart = useChartTheme();

  // Fit the y-axis to the data instead of a fixed [0, 1] so clustered scores
  // aren't squashed into a flat strip (see computeScoreYDomain). Memo runs
  // before the early returns below to keep hook order stable.
  const yDomain = useMemo(
    () => computeScoreYDomain(dataPoints, criteriaNames),
    [dataPoints, criteriaNames],
  );

  if (loading) {
    return <ChartSkeleton title="Score Trends" variant="line" />;
  }

  // Title + (optional) metric toggle, shared by the empty and populated states.
  const header = (
    <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
      <h3 className="text-lg font-semibold">Score Trends</h3>
      {onMetricChange && (
        <div
          className="inline-flex items-center rounded-full border border-border bg-muted/40 p-0.5"
          role="group"
          aria-label="Score metric"
        >
          {SCORE_METRIC_OPTIONS.map((opt) => {
            const active = opt.key === metric;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => onMetricChange(opt.key)}
                aria-pressed={active}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  if (dataPoints.length === 0 || criteriaNames.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        {header}
        <EmptyState
          message="No evaluation data available for chart"
          variant="info"
        />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      {header}
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={dataPoints}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
          <XAxis
            dataKey="date"
            stroke={chart.axis}
            tick={{ fill: chart.axis, fontSize: 12 }}
            tickFormatter={(value: string) => formatDisplayDate(value, bucket)}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            domain={yDomain}
            stroke={chart.axis}
            tick={{ fill: chart.axis, fontSize: 12 }}
            // Recharts derives ticks by floating-point arithmetic off the domain,
            // which surfaces noise like 0.9999999999 — pin every label to 2 dp.
            tickFormatter={(value: number) => value.toFixed(2)}
            label={{
              value: "Score",
              angle: -90,
              position: "insideLeft",
              style: { textAnchor: "middle", fill: chart.axis },
            }}
          />
          <Tooltip content={<LineChartTooltip bucket={bucket} labels={labels} />} />
          <Legend
            wrapperStyle={{ paddingTop: "20px" }}
            iconType="line"
            formatter={(value: string) => {
              // `value` is the series criteria (Line name); show the evaluator name,
              // and preserve the full criteria description on hover via `title`
              // (016 §5 — descriptions moved out of the tooltip onto the legend).
              const label = labels[value] ?? value;
              const truncatedValue =
                label.length > 40 ? label.substring(0, 40) + "..." : label;
              return (
                <span className="text-sm text-muted-foreground" title={value}>
                  {truncatedValue}
                </span>
              );
            }}
          />
          {criteriaNames.map((criteria, index) => {
            const color = CRITERIA_COLORS[index % CRITERIA_COLORS.length];
            const onSelect = onDrill
              ? (payload: any) =>
                  onDrill({
                    type: "criteriaBucket",
                    criteria,
                    bucketStart:
                      typeof payload?.date === "string"
                        ? payload.date
                        : undefined,
                    bucket,
                  })
              : undefined;
            return (
              <Line
                key={criteria}
                type="natural"
                dataKey={criteria}
                stroke={color}
                strokeWidth={2}
                // When drillable, a custom dot carries its own onClick (see
                // makeClickableDot — a Line-level onClick doesn't fire from the
                // dots). The activeDot needs the SAME handler: on hover recharts
                // paints it on top of the regular dot, so a plain activeDot
                // swallows the click you were aiming at the point.
                dot={
                  onSelect
                    ? makeClickableDot({
                        color,
                        radius: 3,
                        keyPrefix: `dot-${criteria}`,
                        onSelect,
                      })
                    : { r: 3 }
                }
                activeDot={
                  onSelect
                    ? makeClickableDot({
                        color,
                        radius: 5,
                        keyPrefix: `adot-${criteria}`,
                        onSelect,
                      })
                    : { r: 5 }
                }
                name={criteria}
                connectNulls={false}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
